/**
 * Mon Espace Types
 * Types for the user planning feature (admin/bureau)
 * Matching the expert preferences availability structure
 */

export type PlanningMode = 'recurrent' | 'week';

export type DayOfWeek = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

export interface TimeSlot {
  start: string;      // HH:MM format
  end: string;        // HH:MM format
  enabled: boolean;
}

export interface DayAvailability {
  startingDomicile: number; // Not used in Mon Espace, but kept for compatibility
  slots: TimeSlot[];        // Array of time slots (using single slot for Mon Espace)
  workLocation?: 'office' | 'remote'; // Work location: office or remote (télétravail)
  isHoliday?: boolean;      // Week mode: this day is a public holiday — defaults to clear, base auto-credited
}

export interface PlanningData extends Record<string, DayAvailability> {
  monday: DayAvailability;
  tuesday: DayAvailability;
  wednesday: DayAvailability;
  thursday: DayAvailability;
  friday: DayAvailability;
  saturday: DayAvailability;
  sunday: DayAvailability;
}

export interface WeekPlanningResponse {
  planning: PlanningData;
  hasOverrides: boolean;
  isPastWeek?: boolean; // True if this week is before the current week
}

export interface PlanningApiResponse {
  success: boolean;
  data?: PlanningData | WeekPlanningResponse;
  message?: string;
}

export interface SavePlanningRequest {
  planning: Partial<PlanningData>;
}

// Helper type for days array
export const DAYS_OF_WEEK: DayOfWeek[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday'
];

// Helper type for day labels
export const DAY_LABELS: Record<DayOfWeek, { fr: string; short: string }> = {
  monday: { fr: 'Lundi', short: 'Lun' },
  tuesday: { fr: 'Mardi', short: 'Mar' },
  wednesday: { fr: 'Mercredi', short: 'Mer' },
  thursday: { fr: 'Jeudi', short: 'Jeu' },
  friday: { fr: 'Vendredi', short: 'Ven' },
  saturday: { fr: 'Samedi', short: 'Sam' },
  sunday: { fr: 'Dimanche', short: 'Dim' }
};

// Default planning values
export const DEFAULT_PLANNING: PlanningData = {
  monday: { startingDomicile: 1, slots: [{ start: '09:00', end: '17:00', enabled: false }], workLocation: 'office' },
  tuesday: { startingDomicile: 1, slots: [{ start: '09:00', end: '17:00', enabled: false }], workLocation: 'office' },
  wednesday: { startingDomicile: 1, slots: [{ start: '09:00', end: '17:00', enabled: false }], workLocation: 'office' },
  thursday: { startingDomicile: 1, slots: [{ start: '09:00', end: '17:00', enabled: false }], workLocation: 'office' },
  friday: { startingDomicile: 1, slots: [{ start: '09:00', end: '17:00', enabled: false }], workLocation: 'office' },
  saturday: { startingDomicile: 1, slots: [{ start: '09:00', end: '17:00', enabled: false }], workLocation: 'office' },
  sunday: { startingDomicile: 1, slots: [{ start: '09:00', end: '17:00', enabled: false }], workLocation: 'office' }
};

/**
 * Private Appointment Types
 * For marking when user is away (meetings, vacations, etc.)
 */

// Frontend format (matches AvailabilityCalendar component expectations)
export interface PrivateAppointment {
  id: string;
  date: string;               // YYYY-MM-DD
  dayOfWeek: string;          // 'monday', 'tuesday', etc.
  startTime: string;          // HH:MM
  endTime: string;            // HH:MM
  title: string;              // Appointment title
  startAddress?: {
    id?: number;              // Domicile address ID
    custom?: string;          // Custom address string
  };
  endAddress?: {
    id?: number;              // Domicile address ID
    custom?: string;          // Custom address string
  };
  travelTimeBefore?: number;  // Minutes of travel time before
  travelTimeAfter?: number;   // Minutes of travel time after
  recurring?: boolean;        // Is this a recurring appointment
  recurrencePattern?: 'weekly' | 'biweekly' | 'monthly'; // Recurrence pattern
  recurrenceEndDate?: string; // YYYY-MM-DD - when recurrence stops
  exceptions?: string[];      // Array of YYYY-MM-DD dates where recurring appointment should not appear
  overrideDate?: string;      // YYYY-MM-DD - specific date for one-time override of recurring appointment
  overrideParentId?: string;  // UUID - link to parent recurring appointment that this overrides
  createdAt?: string;         // ISO timestamp - when appointment was created
  isProfessional?: boolean;   // When true, time counts toward worked hours (e.g., client meeting, training)
}

// Backend format (from API)
export interface PrivateAppointmentDTO {
  id: string;
  date: string;
  dayOfWeek: string;
  startTime: string;
  endTime: string;
  title: string;
  travelTimeBefore?: number;
  travelTimeAfter?: number;
  startAddress?: string;       // Custom address as string
  endAddress?: string;         // Custom address as string
  isRecurring: boolean;
  recurrencePattern: string | null;
  recurrenceEndDate: string | null;
  parentAppointmentId: string | null;
  exceptions: string[];        // Array of YYYY-MM-DD dates
  overrideDate: string | null; // YYYY-MM-DD
  overrideParentId: string | null; // UUID
  createdAt: string | null;    // ISO timestamp
  isProfessional: boolean;     // When true, time counts toward worked hours
}

// Request format for creating/updating appointments
export interface CreateAppointmentRequest {
  date: string;
  dayOfWeek: string;
  startTime: string;
  endTime: string;
  title: string;
  travelTimeBefore?: number;
  travelTimeAfter?: number;
  startAddress?: string;
  endAddress?: string;
  isRecurring?: boolean;
  recurrencePattern?: string | null;
  recurrenceEndDate?: string | null;
  overrideDate?: string | null;      // For one-time overrides of recurring appointments
  overrideParentId?: string | null;  // Link to parent recurring appointment
  isProfessional?: boolean;          // When true, time counts toward worked hours
}

export interface UpdateAppointmentRequest {
  date?: string;
  dayOfWeek?: string;
  startTime?: string;
  endTime?: string;
  title?: string;
  travelTimeBefore?: number;
  travelTimeAfter?: number;
  startAddress?: string;
  endAddress?: string;
  exceptions?: string[];  // For adding/updating exceptions on recurring appointments
  isProfessional?: boolean;  // When true, time counts toward worked hours
}
